import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createSetting,
  fetchGeneralSettings,
  fetchSettingsList,
  fetchSettingsLookup,
  saveGeneralSettings,
  updateSetting,
  updateSettingStatus,
  applyTdsPayments,
  fetchTdsManagement,
} from './settings.api';
import type {
  SettingsListResponse,
  SettingsLookupResource,
  SettingsModuleSlug,
  SettingsStatus,
  TdsApplyChange,
} from './types';

const lookupModules = new Set<SettingsModuleSlug>([
  'roles',
  'vials',
  'charges',
  'test-samples',
  'charge-categories',
  'charge-sections',
  'requisition-sections',
  'wards',
  'departments',
]);

const invalidateModuleAndLookup = async (
  queryClient: ReturnType<typeof useQueryClient>,
  module: SettingsModuleSlug,
) => {
  const invalidations = [queryClient.invalidateQueries({ queryKey: settingsKeys.module(module) })];
  if (lookupModules.has(module)) {
    invalidations.push(queryClient.invalidateQueries({ queryKey: settingsKeys.lookup(module as SettingsLookupResource) }));
  }
  await Promise.all(invalidations);
};

export const settingsKeys = {
  all: ['settings'] as const,
  module: (module: SettingsModuleSlug) => [...settingsKeys.all, module] as const,
  list: (module: SettingsModuleSlug, page: number, limit: number, search: string, status: SettingsStatus | '', sortBy: string | null = null, sortOrder: 'asc' | 'desc' = 'asc', category = '', subCategory = '') =>
    [...settingsKeys.module(module), 'list', { page, limit, search, status, sortBy, sortOrder, category, subCategory }] as const,
  general: () => [...settingsKeys.module('general-settings'), 'detail'] as const,
  lookup: (resource: SettingsLookupResource) => [...settingsKeys.all, 'lookup', resource] as const,
  tdsList: (page: number, limit: number, section: string, billNumber: string, sortBy: string | null = null, sortOrder: 'asc' | 'desc' = 'asc') =>
    [...settingsKeys.module('tds-management'), 'transactions', { page, limit, section, billNumber, sortBy, sortOrder }] as const,
};

export const useSettingsList = (
  module: SettingsModuleSlug,
  page: number,
  limit: number,
  search: string,
  status: SettingsStatus | '',
  sortBy: string | null = null,
  sortOrder: 'asc' | 'desc' = 'asc',
  category = '',
  subCategory = '',
) => useQuery({
  queryKey: settingsKeys.list(module, page, limit, search, status, sortBy, sortOrder, category, subCategory),
  queryFn: () => fetchSettingsList(module, {
    page,
    limit,
    ...(search ? { search } : {}),
    ...(status ? { status } : {}),
    ...(sortBy ? { sortBy, sortOrder } : {}),
    ...(category ? { category } : {}),
    ...(subCategory ? { subCategory } : {}),
  }),
});

export const useGeneralSettings = () => useQuery({
  queryKey: settingsKeys.general(),
  queryFn: fetchGeneralSettings,
  retry: false,
});

export const useSettingsLookup = (resource: SettingsLookupResource, enabled: boolean, params?: { parentId?: string }) => useQuery({
  queryKey: [...settingsKeys.lookup(resource), params ?? {}],
  queryFn: () => fetchSettingsLookup(resource, params),
  enabled,
  staleTime: 5 * 60_000,
});

export const useSaveGeneralSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveGeneralSettings,
    onSuccess: async (record) => {
      queryClient.setQueryData(settingsKeys.general(), record);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: settingsKeys.module('general-settings') }),
        queryClient.invalidateQueries({ queryKey: ['hospital-brand'] }),
      ]);
    },
  });
};

export const useCreateSetting = (module: SettingsModuleSlug) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createSetting(module, payload),
    onSuccess: async () => invalidateModuleAndLookup(queryClient, module),
  });
};

export const useUpdateSetting = (module: SettingsModuleSlug) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recordId, payload }: { recordId: string; payload: Record<string, unknown> }) =>
      updateSetting(module, recordId, payload),
    onSuccess: async () => invalidateModuleAndLookup(queryClient, module),
  });
};

export const useToggleSettingStatus = (module: SettingsModuleSlug) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recordId, status }: { recordId: string; status: SettingsStatus }) =>
      updateSettingStatus(module, recordId, status),
    onMutate: async ({ recordId, status }) => {
      await queryClient.cancelQueries({ queryKey: settingsKeys.module(module) });
      const snapshots = queryClient.getQueriesData<SettingsListResponse>({ queryKey: settingsKeys.module(module) });
      queryClient.setQueriesData<SettingsListResponse>({ queryKey: settingsKeys.module(module) }, (current) => {
        if (!current) return current;
        return {
          ...current,
          data: current.data.map((record) => record.id === recordId ? { ...record, status } : record),
        };
      });
      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      context?.snapshots.forEach(([key, value]) => queryClient.setQueryData(key, value));
    },
    onSettled: async () => invalidateModuleAndLookup(queryClient, module),
  });
};

const normalizedHeaderName = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

const decodeHeaderImage = async (src: string) => {
  const image = new Image();
  image.src = src;
  await image.decode().catch(() => undefined);
};

export const useDocumentHeaderLogo = (headerName: string, enabled = true) => useQuery({
  queryKey: ['document-header-logo', headerName],
  queryFn: async () => {
    const { data } = await fetchSettingsList('document-headers', { page: 1, limit: 100, status: 'active' });
    const expectedName = normalizedHeaderName(headerName);
    const match = data.find((record) => typeof record.headerName === 'string' && normalizedHeaderName(record.headerName) === expectedName);
    const logo = match?.logoDataUrl;
    if (typeof logo !== 'string' || !logo) return null;
    await decodeHeaderImage(logo);
    return logo;
  },
  enabled,
  staleTime: 5 * 60_000,
});

export const useTdsManagement = (
  page: number,
  limit: number,
  section: string,
  billNumber: string,
  sortBy: string | null = null,
  sortOrder: 'asc' | 'desc' = 'asc',
) => useQuery({
  queryKey: settingsKeys.tdsList(page, limit, section, billNumber, sortBy, sortOrder),
  queryFn: () => fetchTdsManagement({
    page,
    limit,
    ...(section ? { section } : {}),
    ...(billNumber ? { billNumber } : {}),
    ...(sortBy ? { sortBy, sortOrder } : {}),
  }),
});

export const useBulkApplyTds = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ changes }: { changes: TdsApplyChange[] }) => applyTdsPayments(changes),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: settingsKeys.module('tds-management') }),
  });
};
